import { useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMainBorrowerPricingState } from '../../../contexts/main-borrower-pricing/MainBorrowerPricingContext';
import { BorrowerGuaranteeFieldNames, BorrowerGuaranteeFormValues } from '../config/BorrowerGuaranteeFormConfig';

/**
 * Hook pour gérer l'état des garanties ITT/IPT et leurs options associées
 * Gère automatiquement les valeurs par défaut et le reset des champs
 */
export const useGuaranteeState = () => {
  const { guaranteesReferential } = useMainBorrowerPricingState();
  const { values, setFieldValue } = useFormikContext<BorrowerGuaranteeFormValues>();

  const activeGuaranteeIds = useMemo(() => {
    return values[BorrowerGuaranteeFieldNames.Guarantees] || [];
  }, [values]);

  // Vérifier si une garantie spécifique est active
  const isGuaranteeActive = useCallback((guaranteeName: string): boolean => {
    const guarantee = guaranteesReferential.find(g => g.name === guaranteeName);
    return guarantee ? activeGuaranteeIds.includes(guarantee.id) : false;
  }, [activeGuaranteeIds, guaranteesReferential]);

  const isITTActive = useMemo(() => isGuaranteeActive('ITT'), [isGuaranteeActive]);
  const isIPTActive = useMemo(() => isGuaranteeActive('IPT'), [isGuaranteeActive]);

  // Track previous states pour détecter les transitions
  const prevITTActiveRef = useRef(isITTActive);
  const prevIPTActiveRef = useRef(isIPTActive);

  // Effet pour gérer les valeurs ITT
  useEffect(() => {
    if (!isITTActive) {
      // ITT désactivé -> reset tous les champs associés
      setFieldValue(BorrowerGuaranteeFieldNames.ContribExempt, null);
      setFieldValue(BorrowerGuaranteeFieldNames.PsyBuyback, null);
      setFieldValue(BorrowerGuaranteeFieldNames.IsCapital, null);
      setFieldValue(BorrowerGuaranteeFieldNames.Deductible, null);
    } else if (!prevITTActiveRef.current) {
      // ITT vient d'être activé -> set valeurs par défaut
      if (values[BorrowerGuaranteeFieldNames.PsyBuyback] == null) {
        setFieldValue(BorrowerGuaranteeFieldNames.PsyBuyback, true);
      }
      if (values[BorrowerGuaranteeFieldNames.Deductible] == null) {
        setFieldValue(BorrowerGuaranteeFieldNames.Deductible, 90);
      }
    }
    prevITTActiveRef.current = isITTActive;
  }, [isITTActive, setFieldValue, values]);

  // Effet pour gérer les valeurs IPT
  useEffect(() => {
    if (isITTActive && !isIPTActive) {
      // ITT actif mais IPT désactivé -> reset isCapital
      setFieldValue(BorrowerGuaranteeFieldNames.IsCapital, null);
    } else if (isIPTActive && !prevIPTActiveRef.current) {
      // IPT vient d'être activé -> set valeur par défaut
      if (values[BorrowerGuaranteeFieldNames.IsCapital] == null) {
        setFieldValue(BorrowerGuaranteeFieldNames.IsCapital, false);
      }
    }
    prevIPTActiveRef.current = isIPTActive;
  }, [isIPTActive, isITTActive, setFieldValue, values]);

  return {
    activeGuaranteeIds,
    isITTActive,
    isIPTActive,
    isGuaranteeActive
  };
};
